import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from './common.js';

export const SyncRunStatusSchema = z.enum(['running', 'success', 'partial', 'failure']);
export type SyncRunStatus = z.infer<typeof SyncRunStatusSchema>;

export const SyncRunCountsSchema = z.object({
  fetched: z.number().int().nonnegative().default(0),
  created: z.number().int().nonnegative().default(0),
  updated: z.number().int().nonnegative().default(0),
  skipped: z.number().int().nonnegative().default(0),
  skipped_segment: z.number().int().nonnegative().default(0),
  // Records ohne auflösbares Kundensegment (Datenlücke, Soft-Skip; kein Fehler/Alarm).
  skipped_no_segment: z.number().int().nonnegative().default(0),
  errors: z.number().int().nonnegative().default(0),
  // PV-052 L3: fehlgeschlagene Ist-Abfragen des Ist-Abgleichs (fehlt ohne Ist-Abgleich, z. B. Delta).
  // Kein `errors`: der Lauf bleibt `success`.
  ist_fehler: z.number().int().nonnegative().optional(),
  // #131: nur im Refresh (`projectKeys`-Zweig) — angefragte Keys, die SF nicht zurückgab.
  // `sf_geloescht` = im SF-Papierkorb, Auftrag automatisch abgeschlossen; die übrigen nur gezählt.
  sf_geloescht: z.number().int().nonnegative().optional(),
  sf_nicht_auffindbar: z.number().int().nonnegative().optional(),
  sf_vorhanden_anderer_key: z.number().int().nonnegative().optional(),
  sf_pruef_fehler: z.number().int().nonnegative().optional(),
});
export type SyncRunCounts = z.infer<typeof SyncRunCountsSchema>;

/**
 * Auslöser eines Sync-Laufs. Deckungsgleich mit dem DB-Enum `sync_run_trigger`:
 * - `scheduler`          — geplanter Delta-Lauf (neue Projekte, CreatedDate-Wasserzeichen)
 * - `scheduler_refresh`  — geplanter Refresh-Lauf über die offenen Projekte
 * - `admin`              — im Admin-Bereich von Hand angestoßener Lauf
 * - `manual`             — Einzelprojekt-Sync aus dem Projektdetail („Aus Salesforce neu laden").
 *                          Eigener Wert statt `admin`, weil das seit #49 keine Admin-Aktion mehr
 *                          ist, sondern jeder Segment-Verantwortliche auslösen darf.
 */
export const SyncRunTriggerSchema = z.enum(['scheduler', 'scheduler_refresh', 'admin', 'manual']);
export type SyncRunTrigger = z.infer<typeof SyncRunTriggerSchema>;

export const SyncRunSchema = z.object({
  sync_run_id: UuidSchema,
  triggered_by: SyncRunTriggerSchema,
  triggered_by_user_id: UuidSchema.optional(),
  start_at: IsoDateTimeSchema,
  end_at: IsoDateTimeSchema.optional(),
  status: SyncRunStatusSchema,
  error_summary: z.string().optional(),
  counts: SyncRunCountsSchema.optional(),
  retry_count: z.number().int().nonnegative().default(0),
  alarm_triggered: z.boolean().default(false),
});
export type SyncRun = z.infer<typeof SyncRunSchema>;
